import { z } from 'zod';
import { chatThemeSchema } from './theme.js';
import {
  interfaceThemeSchema,
  languageSchema,
  type InterfaceThemeId,
  type LanguageCode,
} from './interface.js';
import { encryptedEnvelopeSchema } from './envelope.js';

/** HTTP contract between the web client and the API. Documented in docs/api.md. */

/* ------------------------------------------------------------------------ errors */

export const apiErrorCodes = [
  'bad_request',
  'unauthenticated',
  'forbidden',
  'not_found',
  'conflict',
  'gone',
  'rate_limited',
  'payload_too_large',
  'limit_exceeded',
  'account_suspended',
  'internal',
] as const;
export type ApiErrorCode = (typeof apiErrorCodes)[number];

export interface ApiError {
  error: {
    code: ApiErrorCode;
    /** Human-readable, safe to display. Never contains conversation plaintext. */
    message: string;
    /** Field-level detail for validation failures. */
    details?: Record<string, string>;
  };
}

/* -------------------------------------------------------------------------- auth */

export const requestLoginLinkSchema = z.object({
  email: z.string().email().max(320),
});

/**
 * Preview of a login token. Deliberately a GET that does NOT consume the token,
 * so scanners and preview services cannot complete a login (concept §3).
 */
export interface LoginTokenPreview {
  /** Whether the token is currently redeemable. */
  valid: boolean;
  /** Present only when valid. Partially masked, never the full address. */
  emailHint?: string;
  expiresAt?: string;
  reason?: 'expired' | 'already_used' | 'unknown';
}

export const confirmLoginSchema = z.object({
  token: z.string().min(20).max(200),
});

export interface OwnerSessionResponse {
  owner: OwnerAccount;
  /** Opaque session credential. Also set as an HttpOnly cookie. */
  sessionToken: string;
  expiresAt: string;
}

export interface OwnerAccount {
  id: string;
  email: string;
  createdAt: string;
  suspendedAt: string | null;
  suspensionReason: string | null;
}

/* ----------------------------------------------------------------------- profile */

export const profileUpdateSchema = z.object({
  displayName: z.string().min(1).max(120),
  headline: z.string().max(200).nullable().optional(),
  bio: z.string().max(4000).nullable().optional(),
  theme: chatThemeSchema.optional(),
  /** Which messenger's visual language the owner sees. */
  preferredInterface: interfaceThemeSchema.optional(),
  /** Interface language, and the language messages are translated into. */
  preferredLanguage: languageSchema.optional(),
});

export interface ProfileMediaItem {
  id: string;
  kind: 'photo' | 'video';
  /** Opaque object key. */
  objectKey: string;
  contentType: string;
  sizeBytes: number;
  position: number;
  /** Short-lived presigned URL for display. */
  url: string;
}

/* ------------------------------------------------------------ profile import */

/**
 * Branding suggested by a profile import.
 *
 * A suggestion, not a setting: each field maps onto `chatThemeSchema` and is
 * only applied once the owner accepts the draft. Any value outside the closed
 * set is discarded server-side before it reaches this shape.
 */
export interface BrandingSuggestion {
  accentColour: string | null;
  backgroundColour: string | null;
  surfaceColour: string | null;
  textColour: string | null;
  font: string | null;
  style: string | null;
  corners: string | null;
  rationale: string | null;
}

export interface ProfileDraft {
  displayName: string | null;
  headline: string | null;
  bio: string | null;
  branding: BrandingSuggestion;
  /** What the model could not determine from the source. */
  notes: string[];
  /** The source contained nothing biographical; nothing was drafted. */
  insufficient: boolean;
}

export interface ProfileImportResult {
  /**
   * True when the link could not be read at all — a sign-in wall, a block page
   * or an empty shell. No draft is produced in that case, on purpose: drafting
   * from a login page is fabrication however plausible it reads.
   */
  blocked: boolean;
  reason?: string | null;
  draft: ProfileDraft | null;
  /** Absolute image URLs found on the page, for the owner to choose from. */
  images: string[];
  warnings: string[];
}

export interface OwnerProfile {
  ownerId: string;
  displayName: string;
  headline: string | null;
  bio: string | null;
  theme: z.infer<typeof chatThemeSchema>;
  /**
   * The owner's chosen interface theme. Separate from `theme`, which is the
   * owner's *branding* — the colours a visitor sees. This is which messenger's
   * layout and visual language the interface itself adopts.
   */
  preferredInterface: InterfaceThemeId;
  /**
   * Interface language, and the language incoming messages are translated into.
   *
   * `null` means the owner has never chosen one, which is the signal for the
   * client to adopt the browser's language and record it.
   */
  preferredLanguage: LanguageCode | null;
  media: ProfileMediaItem[];
  /**
   * Optional capabilities this deployment actually has configured.
   *
   * The client uses these to decide what to offer. A feature that is off is
   * simply absent from the interface rather than present and broken.
   */
  features: { profileImport: boolean };
  updatedAt: string;
}

/** What a visitor is shown before joining. Owner-published, not E2EE (concept §6). */
export interface PublicProfile {
  displayName: string;
  headline: string | null;
  bio: string | null;
  theme: z.infer<typeof chatThemeSchema>;
  media: Array<Omit<ProfileMediaItem, 'id'>>;
}

/* ------------------------------------------------------------------ invitations */

export const createInvitationSchema = z.object({
  /**
   * Client attestation that the conversation key was generated and persisted
   * locally before the invitation was created (concept §5). The server refuses
   * to create an invitation without it.
   */
  keyStorage: z.object({
    persisted: z.literal(true),
    /** How the key is retained locally, for the owner-facing explanation. */
    mechanism: z.enum(['indexeddb']),
  }),
  /** Optional owner-only label. Stored as ciphertext; opaque to the server. */
  label: encryptedEnvelopeSchema.optional(),
});

export interface CreatedInvitation {
  conversationId: string;
  /**
   * The invitation path only — no origin, no fragment. The browser appends the
   * conversation key as a fragment locally; the server never sees it.
   */
  invitationPath: string;
  /** The bearer invitation token. Returned once, to the creating browser only. */
  invitationToken: string;
  createdAt: string;
  inviteExpiresAt: string;
}

export type ConversationStatus = 'pending' | 'active' | 'expired' | 'deleted';

/**
 * Operational status only. Never contains a decrypted name or message preview,
 * on any device (concept §5).
 */
export interface ConversationSummary {
  id: string;
  status: ConversationStatus;
  createdAt: string;
  inviteExpiresAt: string;
  activatedAt: string | null;
  expiresAt: string | null;
  lastActivityAt: string | null;
  messageCount: number;
  /** Encrypted visitor display name. Undecryptable without the local key. */
  visitorName: z.infer<typeof encryptedEnvelopeSchema> | null;
  /** Encrypted owner-only label. */
  label: z.infer<typeof encryptedEnvelopeSchema> | null;
}

/* ----------------------------------------------------------------------- visitor */

/** Result of an ordinary GET on an invitation. Does not activate or claim it (concept §4). */
export interface InvitationPreview {
  state: 'joinable' | 'claimed' | 'expired' | 'unknown';
  /** Owner-published profile, shown before joining. Present when joinable. */
  profile?: PublicProfile;
  /** Present when joinable, so the visitor knows how long the conversation will last. */
  activeTtlSeconds?: number;
}

export const joinInvitationSchema = z.object({
  /**
   * The visitor display name, already encrypted in the browser. It is
   * conversation content, not metadata (concept §6), so the server never sees
   * the plaintext.
   */
  visitorName: encryptedEnvelopeSchema,
});

export interface JoinResult {
  conversationId: string;
  /** Opaque visitor session credential. Stored locally; never in a URL. */
  sessionToken: string;
  activatedAt: string;
  expiresAt: string;
  profile: PublicProfile;
}

/* ------------------------------------------------------------------- attachments */

export const requestUploadSchema = z.object({
  /** Ciphertext byte length, which is what actually gets stored. */
  sizeBytes: z.number().int().positive(),
});

export interface UploadTicket {
  /** Opaque, server-generated object key. Carries no filename (concept §6). */
  objectKey: string;
  /** Short-lived presigned PUT URL. */
  uploadUrl: string;
  expiresAt: string;
  /** Content type the client must send. Always the opaque ciphertext type. */
  requiredContentType: string;
}

export interface DownloadTicket {
  objectKey: string;
  downloadUrl: string;
  expiresAt: string;
  sizeBytes: number;
}

/* ----------------------------------------------------------------------- reports */

export const submitReportSchema = z.object({
  reason: z.enum(['abuse', 'harassment', 'illegal_content', 'spam', 'other']),
  /**
   * Free text the reporting participant chooses to submit. Concept §11 allows a
   * report to carry content the participant voluntarily provides; it is stored
   * in plaintext and is explicitly outside the E2EE claim (concept §6).
   */
  details: z.string().max(4000).optional(),
});

export interface SubmittedReport {
  id: string;
  createdAt: string;
}

/* ------------------------------------------------------------------------ health */

export interface HealthResponse {
  status: 'ok' | 'degraded';
  service: string;
  version: string;
  uptimeSeconds: number;
}

export interface ReadinessResponse {
  status: 'ready' | 'not_ready';
  checks: Record<string, { ok: boolean; detail?: string }>;
}
